import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  LOCALE_ID,
  Provider,
  EnvironmentProviders,
  provideZoneChangeDetection,
} from '@angular/core';

// Sem isso o app inteiro formata em en-US: os pipes `number`/`currency` (45 e
// 200 usos) renderizavam "R$ 39.90" com ponto. Componentes soltos registravam
// pt-BR localmente (restaurant-lib, sport-lib), o que mascarava o problema em
// algumas telas e deixava o resto errado — inclusive a loja de módulos.
registerLocaleData(localePt);
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { provideRouter, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { metaReducers, reducers } from '@lessor/store/index';
import { provideAuth } from '@libs/auth-lib';
// `climbing-lib` é lazy-loaded em app-routes, mas `provideClimbingSync`
// precisa rodar no boot (APP_INITIALIZER) pra instanciar o sync engine
// e escutar mudanças de conectividade antes da tela offline ser aberta.
// Import estático é intencional aqui. Mover o provider pra uma lib
// não-lazy é refactor pendente — ver roadmap offline.
// eslint-disable-next-line @nx/enforce-module-boundaries
import { provideClimbingSync } from '@libs/climbing';
import { IdempotencyKeyInterceptor } from '@libs/shared/offline-sync-engine-lib';
import { CrashlyticsService, CrashlyticsErrorHandler } from '@libs/crashlytics';
import { Canal } from '@libs/entity-lib';
import { provideNotifications } from '@libs/notifications';
// Providers registrados no bootstrap (client-360 contributions) — precisam ser
// estáticos mesmo as libs sendo lazy nas rotas. Ver roadmap offline.
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AULAS_CLIENT_CONTRIBUTION_PROVIDER,
  PACOTES_CLIENT_CONTRIBUTION_PROVIDER,
  TERMOS_CLIENT_CONTRIBUTION_PROVIDER,
} from '@libs/client';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TREINOS_CLIENT_CONTRIBUTION_PROVIDER } from '@libs/training';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { DAY_USE_CLIENT_CONTRIBUTION_PROVIDER } from '@libs/day-use';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ACCOMMODATION_CLIENT_CONTRIBUTION_PROVIDER } from '@libs/accommodation';
import { commonEnvironment, lessorEnvironment } from '@libs/shared/util-environments';
import { appRoutes } from './app-routes';
import { CoreModule } from './core/core.module';
import { InterceptorProvider } from './providers/interceptor/interceptor';
import {
  AUTH_REFRESH_DEPENDENCIES,
  CLIENT_PICKER_HANDLER,
  CLIENT_PROFILE_OPENER,
  DEFAULT_INTERCEPTOR_CONFIG,
  EVENT_CREATOR_HANDLER,
  FIND_PLAYER_OPENER,
  isFcmWebSupported,
  QUICK_REGISTER_OPENER,
  SIGNATURE_VIEW_OPENER,
  SLOT_PICKER_HANDLER,
  SPORT_CENTER_TIMEZONE_PROVIDER,
} from '@libs/util-services';
import { LessorClientPickerHandler } from './core/components/client-profile/client-picker-handler.service';
import { LessorEventCreatorHandler } from './core/components/event-creator/event-creator-handler.service';
import { LessorClientProfileOpener } from './core/components/client-profile/client-profile-opener.service';
import { LessorFindPlayerOpener } from './core/components/client-profile/find-player-opener.service';
import { LessorQuickRegisterOpener } from './core/components/client-profile/quick-register-opener.service';
import { LessorSignatureViewOpener } from './core/components/client-profile/signature-view-opener.service';
import { LessorSlotPickerHandler } from './core/components/slot-picker/slot-picker-handler.service';
import { LessorSportCenterTimezoneProvider } from './core/services/timezone/sport-center-timezone.provider';
import { AuthFacade } from '@libs/auth-lib';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { ProfilelLessorStateModule } from '@libs/settings-state';
import { VibrationService } from '@libs/util-services';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { provideServiceWorker } from '@angular/service-worker';
import { TranslationLibModule } from '@libs/translation';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom([
      StoreModule.forRoot(reducers, { metaReducers }),
      IonicModule.forRoot({ innerHTMLTemplatesEnabled: true }),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: lessorEnvironment.production,
      }),
      EffectsModule.forRoot([]),
      CoreModule,
      ProfilelLessorStateModule,
      TranslationLibModule,
    ]),
    provideAuth({ canal: Canal.SPORT_CENTER }),
    provideNotifications(),
    provideFirebaseApp(() =>
      initializeApp(commonEnvironment.apps[Canal.SPORT_CENTER].firebaseConfig)
    ),
    provideFirestore(() => getFirestore()),
    // FCM Web só faz sentido em browser/PWA com Service Worker + PushManager + Notification.
    // In-app browsers (Instagram, Facebook, TikTok, LinkedIn) crasham em getMessaging()
    // mesmo retornando 'web' pro Capacitor — por isso o guard usa feature detection +
    // UA + flag cooperativa setada pelo white-screen-fallback.js.
    // Mobile (Capacitor nativo) usa @capacitor/push-notifications (ver NotificationListenersService).
    ...((isFcmWebSupported() ? [provideMessaging(() => getMessaging())] : []) as (
      Provider | EnvironmentProviders
    )[]),
    provideIonicAngular({ mode: 'ios' }),
    provideRouter(
      appRoutes,
      withComponentInputBinding()
      // SEM blocking nav: app sem SSR/hydration — evita travar o 1� paint
      // esperando a rota lazy resolver (tela branca no 4G lento).
    ),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: HTTP_INTERCEPTORS, useClass: InterceptorProvider, multi: true },
    // Anexa Idempotency-Key em POST/PUT/PATCH pra /api/climbing/. Sem key
    // explícita no context, gera UUID por request (protege double-click).
    // Sync engine usa `withIdempotencyKey(draft.id)` pra garantir mesma key
    // entre 1ª tentativa e retries — backend dedupa via Redis (TTL 24h).
    { provide: HTTP_INTERCEPTORS, useClass: IdempotencyKeyInterceptor, multi: true },
    {
      provide: AUTH_REFRESH_DEPENDENCIES,
      useFactory: (authFacade: AuthFacade) => ({
        baseUrl: lessorEnvironment.baseUrl,
        isProduction: lessorEnvironment.production,
        config: {
          ...DEFAULT_INTERCEPTOR_CONFIG,
          onAuthFailure: () => {
            authFacade.setExpiredToken(true);
            authFacade.logout();
          },
        },
      }),
      deps: [AuthFacade],
    },
    {
      provide: FIREBASE_OPTIONS,
      useValue: commonEnvironment.apps[Canal.SPORT_CENTER].firebaseConfig,
    },
    CrashlyticsService,
    // Captura global de exceções não tratadas → Crashlytics (recordException).
    { provide: ErrorHandler, useClass: CrashlyticsErrorHandler },
    VibrationService,
    // SLOT_PICKER_HANDLER no root (vs route-level): client-360, scheduler legacy
    // e qualquer outra surface podem precisar abrir o picker, não só /agenda.
    { provide: SLOT_PICKER_HANDLER, useClass: LessorSlotPickerHandler },
    // EVENT_CREATOR_HANDLER no root pelo mesmo motivo do SLOT_PICKER acima.
    // Enquanto ficou só na rota /agenda, o botão "Agendar" do client-360 era
    // clique morto: o modal é criado pelo ModalController (providedIn root),
    // então o injector dele é o RAIZ e nunca enxerga provider de rota — o
    // guard de onAgendar() batia no handler null e só logava warn.
    { provide: EVENT_CREATOR_HANDLER, useClass: LessorEventCreatorHandler },
    // SPORT_CENTER_TIMEZONE_PROVIDER no root (vs route-level): a agenda de
    // aulas (class-management-lib) e qualquer outra surface com o marcador
    // "agora" precisam do fuso do CE, não só a /agenda das quadras.
    { provide: SPORT_CENTER_TIMEZONE_PROVIDER, useExisting: LessorSportCenterTimezoneProvider },
    // CLIENT_PROFILE_OPENER + CLIENT_PICKER_HANDLER: quebram ciclo util-ui ↔ client-lib.
    // Util-ui não importa mais @libs/client diretamente — chama via token.
    { provide: CLIENT_PROFILE_OPENER, useClass: LessorClientProfileOpener },
    { provide: CLIENT_PICKER_HANDLER, useClass: LessorClientPickerHandler },
    // FIND_PLAYER_OPENER: novo opener unificado (segments + lista inline).
    // Substitui o stack util-ui-page → client-picker-modal por uma única tela.
    { provide: FIND_PLAYER_OPENER, useClass: LessorFindPlayerOpener },
    // QUICK_REGISTER_OPENER: cadastro rápido em modal EMPILHADO — a rota
    // /add-athlete renderiza por baixo quando a origem é um modal (event-creator).
    { provide: QUICK_REGISTER_OPENER, useClass: LessorQuickRegisterOpener },
    // SIGNATURE_VIEW_OPENER: badge "Termo" do card do jogador (util-ui) abre o
    // detalhe da assinatura, que mora na client-lib — mesmo motivo do profile opener.
    { provide: SIGNATURE_VIEW_OPENER, useClass: LessorSignatureViewOpener },
    // Cliente 360: contribution do módulo Treinos (segmento "Treinos" + aba lazy).
    PACOTES_CLIENT_CONTRIBUTION_PROVIDER,
    // Aulas (matrículas) e Assinaturas (termos) voltaram: a tela antiga do cliente
    // tinha as duas abas e a migração para o 360 as deixou para trás.
    AULAS_CLIENT_CONTRIBUTION_PROVIDER,
    TERMOS_CLIENT_CONTRIBUTION_PROVIDER,
    TREINOS_CLIENT_CONTRIBUTION_PROVIDER,
    DAY_USE_CLIENT_CONTRIBUTION_PROVIDER,
    ACCOMMODATION_CLIENT_CONTRIBUTION_PROVIDER,
    // Ionic 8.x ainda não suporta zoneless (issue ionic-team/ionic-framework#30804).
    // Mantemos Zone.js até Ionic 9 sair com suporte oficial.
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideServiceWorker(lessorEnvironment.serviceWorker, {
      enabled: lessorEnvironment.enabledSeviceWorker && isFcmWebSupported(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // Sync engine do offline-first da escalada: registra APP_INITIALIZER que
    // instancia o LessorClimbingSyncService no boot — sem isso o listener de
    // connectivity só roda quando a tela offline é aberta. Ver climbing-lib.
    // `EnvironmentProviders` não é iterável — passar direto, sem spread.
    provideClimbingSync(),
  ],
};
